'use client';

import Link from 'next/link';
import { Box, Button, Container, Grid, Typography } from '@mui/material';
import { MarketingLayeredStack, type MarketingLayer } from '@/components/marketing/MarketingLayeredStack';
import { MarketingSpotlightImage } from '@/components/marketing/MarketingSpotlightImage';

type Props = {
  headline: string;
  body: string;
  /** e.g. "FloAI Create features:" */
  featureLabel?: string;
  featureBullets?: string[];
  /** Hootsuite-style fanned stack (preferred) */
  layers?: MarketingLayer[];
  /** Single spotlight fallback */
  imageSrc?: string;
  imageAlt?: string;
  imageRight?: boolean;
  linkHref?: string;
  linkLabel?: string;
};

export function MarketingFeatureRow({
  headline,
  body,
  featureLabel,
  featureBullets,
  layers,
  imageSrc,
  imageAlt,
  imageRight = true,
  linkHref,
  linkLabel,
}: Props) {
  const textCol = (
    <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ py: { xs: 1, md: 3 }, px: { xs: 0, md: 2 }, maxWidth: 520 }}>
        <Typography component="h2" variant="h4" fontWeight={700} sx={{ mb: 1.5, fontSize: { xs: '1.5rem', md: '2rem' } }}>
          {headline}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          {body}
        </Typography>
        {featureBullets && featureBullets.length > 0 ? (
          <Box sx={{ mt: 2.5 }}>
            {featureLabel ? (
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                {featureLabel}
              </Typography>
            ) : null}
            <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
              {featureBullets.map((item) => (
                <Typography component="li" variant="body2" key={item} sx={{ mb: 0.75, lineHeight: 1.6 }}>
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null}
        {linkHref && linkLabel ? (
          <Button component={Link} href={linkHref} variant="outlined" size="small" sx={{ mt: 2 }}>
            {linkLabel}
          </Button>
        ) : null}
      </Box>
    </Grid>
  );

  const visualCol = (
    <Grid item xs={12} md={6} sx={{ width: '100%' }}>
      {layers && layers.length > 0 ? (
        <MarketingLayeredStack layers={layers} mirror={!imageRight} />
      ) : imageSrc && imageAlt ? (
        <MarketingSpotlightImage src={imageSrc} alt={imageAlt} maxWidth={{ xs: 340, md: '100%' }} />
      ) : null}
    </Grid>
  );

  return (
    <Box component="section" sx={{ py: { xs: 5, md: 8 }, bgcolor: 'background.default' }}>
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Grid container spacing={{ xs: 3, md: 6 }} alignItems="center">
          {imageRight ? (
            <>
              {textCol}
              {visualCol}
            </>
          ) : (
            <>
              {visualCol}
              {textCol}
            </>
          )}
        </Grid>
      </Container>
    </Box>
  );
}
